import React, { useCallback, useEffect } from 'react';

import { graphql, PageProps } from 'gatsby';

import {
  ConsultationRequestBody,
  ConversionFormData,
  CRM_RequestType,
  Market,
} from '@mewa/types';

import t from '../../../../libs/translate';
import {
  formExperiment,
  FormVariant,
} from '../../../a-b-testing/form-experiment';
import { NavigationData } from '../../../components/header/types';
import { CantoImage } from '../../../components/strapi/canto-image';
import { setFeatureDev, setIsLocalEnv } from '../../../helpers/feature-dev';
import { useBrowserNavigationUnload } from '../../../hooks/use-browser-navigation-unload';
import { LayoutType } from '../../../layouts/types';
import { sendFormToBackend } from '../../../send-forms/send-forms';
import { GlobalPageContext } from '../../../types';
import { Conversion as ConversionNew } from '../a-b-test-new-variant/components/conversion';
import { useFormState as useFormStateNew } from '../a-b-test-new-variant/hooks/use-form-state';
import { LoadingFormExperiment } from '../a-b-test-new-variant/loading-form-experiment';
import { Conversion as ConversionOld } from '../components/conversion';
import { HashNavigation } from '../hash-navigation';
import {
  getFormSideImagesWithStrapiCantoImage,
  trackFormStep,
} from '../helper';
import { useFormState as useFormStateOld } from '../hooks/use-form-state';
import { parseDefaultFormHash } from '../location-hash-parser';
import { ConversionTranslations } from '../translations-query';

export { Head } from '../../../layouts/head';

export const query = graphql`
  fragment BookConsultationFragment on Query {
    strapiBookConsultation(locale: { eq: $locale }) {
      thankYouImage {
        ...CantoImageFragment
      }
      sideImage {
        ...CantoImageFragment
      }
    }
  }

  query ($marketRegex: String!, $lang: String!, $locale: String!) {
    ...NavigationFragment
    ...ConversionTranslationsFragment
    ...BookConsultationFragment
  }
`;

type Props = NavigationData & {
  translations: ConversionTranslations;

  strapiBookConsultation: {
    thankYouImage: CantoImage;
    sideImage: CantoImage;
  };
};

const trackStepChange = (step: number) => {
  trackFormStep(step + 1, 'contact');
};

export const Contact = (props: PageProps<Props, GlobalPageContext>) => {
  setFeatureDev(props.pageContext.featureDev);
  setIsLocalEnv(props.pageContext.isLocal);

  if (props.pageContext.market === Market.IT) {
    return <ContactABTest {...props} />;
  }
  return <ContactWithoutABTest {...props} />;
};

const ContactWithoutABTest = (props: PageProps<Props, GlobalPageContext>) => {
  const handleSubmit = (formData: ConversionFormData) => {
    const requestData: ConsultationRequestBody = {
      ...formData,
      requestType: CRM_RequestType.Consultation,
      language: props.pageContext.lang,
      market: props.pageContext.market,
    };

    return sendFormToBackend(requestData);
  };

  return <ContactComponentA {...props} onSubmit={handleSubmit} />;
};

const ContactABTest = (props: PageProps<Props, GlobalPageContext>) => {
  const { variant: formVariant, isLoading } = formExperiment.useVariant();

  const navigateAwayFromForm = useCallback(() => {
    formExperiment.reset();
  }, []);

  const abortForm = () => {
    formExperiment.sendEvent(false);
  };

  useBrowserNavigationUnload(() => {
    abortForm();
  });

  useEffect(() => {
    return () => {
      navigateAwayFromForm();
    };
  }, [navigateAwayFromForm]);

  const handleSubmit = (formData: ConversionFormData) => {
    const requestData: ConsultationRequestBody = {
      ...formData,
      requestType: CRM_RequestType.Consultation,
      language: props.pageContext.lang,
      market: props.pageContext.market,
    };

    formExperiment.sendEvent(true);
    return sendFormToBackend(requestData);
  };

  if (isLoading) {
    const formSideImages = getFormSideImagesWithStrapiCantoImage(
      props.data.strapiBookConsultation.sideImage,
      {},
    );

    return (
      <LoadingFormExperiment
        imageSrc={formSideImages.desktop}
        mobileImageSrc={formSideImages.mobile}
      />
    );
  }

  if (formVariant === FormVariant.new) {
    return <ContactComponentB {...props} onSubmit={handleSubmit} />;
  }

  return <ContactComponentA {...props} onSubmit={handleSubmit} />;
};

const ContactComponentA = ({
  data: {
    translations,
    navigationLinks,
    navigationServiceSegments,
    strapiBookConsultation,
  },
  onSubmit,
  pageContext,
}: PageProps<Props, GlobalPageContext> & {
  onSubmit: (formData: ConversionFormData) => Promise<void>;
}) => {
  const formState = useFormStateOld(CRM_RequestType.Consultation);

  const formSideImages = getFormSideImagesWithStrapiCantoImage(
    strapiBookConsultation.sideImage,
    {},
  );

  return (
    <HashNavigation
      stepForHash={parseDefaultFormHash}
      maxAllowedStep={formState.maxStep}
      state={formState}
      renderStep={(step, formState) => {
        return (
          <ConversionOld
            selectedStep={step}
            formState={formState}
            formType={CRM_RequestType.Consultation}
            market={pageContext.market}
            lang={pageContext.lang}
            onSubmit={onSubmit}
            translations={translations}
            imageSrc={formSideImages.desktop}
            mobileImageSrc={formSideImages.mobile}
            formTitle={t(
              translations,
              'general_form_what_can_we_do_for_you_label',
            )}
            trackingEvent="form_contact_submit"
            langPrefix={pageContext.langPrefix}
            navigationLinks={navigationLinks}
            serviceSegmentLinks={navigationServiceSegments.nodes}
            thankYouImage={strapiBookConsultation.thankYouImage}
            featureDev={pageContext.featureDev}
          ></ConversionOld>
        );
      }}
      onStepChange={trackStepChange}
    />
  );
};

const ContactComponentB = ({
  data: {
    translations,
    navigationLinks,
    navigationServiceSegments,
    strapiBookConsultation,
  },
  onSubmit,
  pageContext,
}: PageProps<Props, GlobalPageContext> & {
  onSubmit: (formData: ConversionFormData) => Promise<void>;
}) => {
  const formState = useFormStateNew(CRM_RequestType.Consultation);

  const formSideImages = getFormSideImagesWithStrapiCantoImage(
    strapiBookConsultation.sideImage,
    {},
  );

  return (
    <HashNavigation
      stepForHash={parseDefaultFormHash}
      maxAllowedStep={formState.maxStep}
      state={formState}
      renderStep={(step, formState) => {
        return (
          <ConversionNew
            selectedStep={step}
            formState={formState}
            formType={CRM_RequestType.Consultation}
            market={pageContext.market}
            lang={pageContext.lang}
            onSubmit={onSubmit}
            translations={translations}
            imageSrc={formSideImages.desktop}
            mobileImageSrc={formSideImages.mobile}
            formTitle={t(
              translations,
              'general_form_what_can_we_do_for_you_label',
            )}
            trackingEvent="form_contact_submit"
            langPrefix={pageContext.langPrefix}
            navigationLinks={navigationLinks}
            serviceSegmentLinks={navigationServiceSegments.nodes}
            thankYouImage={strapiBookConsultation.thankYouImage}
          ></ConversionNew>
        );
      }}
      onStepChange={trackStepChange}
    />
  );
};

Contact.layout = LayoutType.Minimal;

export default Contact;
